import React from 'react';
import {
  Typography,
  Divider,
  Card,
  CardContent,
  CardActions,
  Box,
  Stack,
} from '@mui/material';
import PropTypes from 'prop-types';
import ActuatorControl from '../../../ActuatorControl';
import { featurePropTypes } from '../../types';
import ActuatorIcon from '../../../ActuatorIcon';
import { Zigbee2MQTTUtils } from '../../../../utils/zigbeeUtils';
import ActuatorFeatureState from '../../../ActuatorFeatureState';
import { linkqualityFilter } from '../../utils/filters';
import CustomTooltip from '../../../shared/atoms/CustomTooltip';
import { StatusSummary } from '../StatusSummary';
import { useActuatorContext } from '../../../../context/ActuatorContext';
import ConfirmingDisplay from '../ConfirmingDisplay';
import ActuatorIconWithStatus from '../../../ActuatorIconWithStatus';

const MiniControlPanel = ({
  gateway,
  deviceId,
  deviceName,
  icon,
  features,
  readOnly,
  noHeader,
}) => {
  const { state } = useActuatorContext();
  const mainFeatures = features.filter(f => !linkqualityFilter(f));

  return (
    <Card
      sx={{
        height: '100%',
        flex: '1 0 30%',
        minWidth: 'min-content',
        filter: readOnly && 'grayscale(0.5)',
        opacity: readOnly && '0.8',
        display: 'flex',
        flexDirection: 'column',
        border: 'none',
      }}
    >
      {!noHeader && (
        <CardContent
          sx={{
            padding: '1rem 1.25rem',
          }}
        >
          <Box display="flex" alignItems="center" gap={1} marginBottom={1}>
            <ActuatorIconWithStatus
              type="library"
              name={icon}
              fontSize="small"
              features={mainFeatures}
            />
            <Typography variant="body2" fontSize="0.9rem">
              {deviceName}
            </Typography>
          </Box>
          <Divider
            sx={{
              borderColor: theme => theme.palette.primary.main,
              borderWidth: '1px',
            }}
          />
        </CardContent>
      )}
      <CardActions
        sx={{
          paddingTop: 0,
          padding: '0px 1.25rem',
          flexGrow: 1,
          overflowY: 'auto',
        }}
      >
        <Stack
          sx={{
            width: '100%',
            height: '100%',
            gap: 1,
          }}
        >
          {mainFeatures.map(feature => {
            const stateId = `${gateway}-${deviceId}-${feature.property}`;
            const { confirming = false } = state[stateId] ?? {};
            return (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={3}
                key={feature.property}
              >
                <Box
                  width={0}
                  flex={1}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <ActuatorIcon
                    exposes={features}
                    name={feature.property}
                    fontSize="small"
                  />
                  <CustomTooltip title={feature.description}>
                    <Typography
                      variant="body2"
                      sx={{ cursor: 'help', textAlign: 'left' }}
                    >
                      {feature.label}
                    </Typography>
                  </CustomTooltip>
                  <ConfirmingDisplay
                    confirming={confirming}
                    mini
                    lastKnownDate={feature.reading?.date}
                    hasState={Zigbee2MQTTUtils.hasState(feature.access)}
                  />
                </Box>
                <Box width={0} flex={1} display="flex" justifyContent="end">
                  {Zigbee2MQTTUtils.hasSet(feature.access) ? (
                    <ActuatorControl
                      gateway={gateway}
                      deviceId={deviceId}
                      deviceName={deviceName}
                      feature={feature}
                      readOnly={readOnly}
                      mini
                    />
                  ) : (
                    <ActuatorFeatureState reading={feature.reading} />
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </CardActions>
      <CardContent
        sx={{
          padding: '1rem 1.25rem',
          '& > div': {
            flex: 1,
            justifyContent: 'end',
            alignItems: 'end',
          },
        }}
      >
        <StatusSummary features={features} showLabels />
      </CardContent>
    </Card>
  );
};

MiniControlPanel.propTypes = {
  readOnly: PropTypes.bool,
  noHeader: PropTypes.bool,
  gateway: PropTypes.string,
  deviceId: PropTypes.string,
  deviceName: PropTypes.string,
  icon: PropTypes.string,
  features: PropTypes.arrayOf(featurePropTypes),
};

export default MiniControlPanel;
